/**
 * Same resolution as `gobodoConfig` / `getCustomSuffix` but env-only so callers (and Jest mocks of
 * the default Gobodo config export) do not need a `getCustomSuffix` mock.
 */
function gobodoCustomSuffixFromEnv(): string {
  return (
    process.env.GOBODO_CUSTOM_SUFFIX ||
    process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ||
    process.env.NEXT_PUBLIC_GOBODO_CUSTOM_SUFFIX ||
    '__c'
  );
}

/**
 * Suffixes that may already appear on Gobodo `key` / Salesforce ids from other envs (must not
 * be stacked with the current env suffix when calling `getObjectByKey`, etc.).
 */
const ADDITIONAL_KNOWN_GOBODO_KEY_SUFFIXES: readonly string[] = [
  '__c',
  '__stg_c',
  '__stg3_c',
  '__stg5_c',
  '__dev_c',
  '__mj_c',
];

function allKnownSuffixesSortedLongestFirst(): string[] {
  return Array.from(
    new Set([gobodoCustomSuffixFromEnv(), ...ADDITIONAL_KNOWN_GOBODO_KEY_SUFFIXES].filter(Boolean))
  ).sort((a, b) => b.length - a.length);
}

/**
 * Collapses accidental `id__stg5_c__stg5_c` (or `__c__c`) from callers that append the env suffix
 * before `getObjectByKey` even when the id was already suffixed.
 */
function stripDuplicateTrailingSuffixes(key: string): string {
  let k = key;
  let changed = true;
  // Repeat until no segment `suffix+suffix` remains at the end (handles triple+ stacks).
  while (changed) {
    changed = false;
    for (const suf of allKnownSuffixesSortedLongestFirst()) {
      const low = k.toLowerCase();
      const doubled = (suf + suf).toLowerCase();
      if (low.length >= doubled.length && low.endsWith(doubled)) {
        k = k.slice(0, -suf.length);
        changed = true;
        break;
      }
    }
  }
  return k;
}

function hasKnownGobodoObjectKeySuffix(key: string): boolean {
  const k = key.toLowerCase();
  const envSuf = gobodoCustomSuffixFromEnv();
  if (envSuf && k.endsWith(envSuf.toLowerCase())) return true;
  for (const s of ADDITIONAL_KNOWN_GOBODO_KEY_SUFFIXES) {
    if (k.endsWith(s.toLowerCase())) return true;
  }
  return false;
}

/**
 * `xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx` (hex) — Gobodo-generated keys for app-created objects
 * (e.g. user-created CS_Audit) are not Salesforce ids and must not receive `GOBODO_CUSTOM_SUFFIX`.
 */
const PLAIN_UUID_KEY_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isGobodoGraphObjectKeyPlainUuid(key: string): boolean {
  const s = String(key ?? '').trim();
  if (!s) return false;
  return PLAIN_UUID_KEY_RE.test(s);
}

/**
 * Normalizes a record `key` for `GraphObjectsApi.getObjectByKey` and graph cache keys: Gobodo
 * stores custom object keys as SF id + `REACT_APP_GOBODO_CUSTOM_SUFFIX` (e.g. `a1X…__stg5_c`).
 * Callers often pass a bare 15- or 18-char Salesforce id; the API path must use the suffixed form.
 * Purely numeric internal ids are left unchanged. Backend-generated UUID string keys (non-SF) are
 * also left unchanged (see {@link isGobodoGraphObjectKeyPlainUuid}).
 */
export function ensureGobodoCustomSuffixOnRecordKey(key: string): string {
  let s = String(key ?? '').trim();
  if (!s) return s;
  s = stripDuplicateTrailingSuffixes(s);
  if (!s) return s;
  if (/^[0-9]+$/.test(s)) return s;
  if (isGobodoGraphObjectKeyPlainUuid(s)) return s;
  if (hasKnownGobodoObjectKeySuffix(s)) return s;
  return `${s}${gobodoCustomSuffixFromEnv()}`;
}
